import { useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import Header from "../Header";
import Field from "../../../../../Settings/Content/Components/Field";
import TrashBucketIcon from "../../../../../../Components/Icon-SVG/TrashBucketIcon";
import Tooltip from "../../../../../../Components/common/Tooltip/Tooltip";
import Backdrop from "../../../../../../Components/UI/Backdrop/Backdrop";

import { RequestUpdateAgentPolicy } from "../../../../../../store/actions/integrationsActions";
import { addNotification } from "../../../../../../store/actions/notifierActions";

const MIN_MAX_VALUES = {
  minWeight: [0, 100],
  minFileSize: [1500, 4500],
  maxFileSize: [20800000, 62400000],
  memMinFileSize: [4000, 12000],
  memMaxFileSize: [30300000, 90900000],
};

const SCALAR_FIELDS = [
  "customRulesEnabled",
  "minWeight",
  "minFileSize",
  "maxFileSize",
  "memMinFileSize",
  "memMaxFileSize",
];
const BOOL_FIELDS = ["customRulesEnabled"];
const CSV_FIELDS = ["categories", "excludedRulePrefixes"];

const toBool = (v) => v === true || v === "true" || v === "True";
const toArray = (v) =>
  typeof v === "string"
    ? v
        .split(",")
        .map((s) => s.trim())
        .filter(Boolean)
    : Array.isArray(v)
      ? v
      : [];

const YaraForm = ({ selectedHost }) => {
  const dispatch = useDispatch();

  const policy = useSelector((state) => state.integrations.agentPolicy);
  const updatePolicyLoading = useSelector((state) => state.integrations.updatePolicyLoading);

  const backend = policy?.settings?.yara;

  const [yaraUI, setYaraUI] = useState({
    ...backend,
    customRulesEnabled: toBool(backend?.customRulesEnabled),
    categories: toArray(backend?.categories),
    excludedRulePrefixes: toArray(backend?.excludedRulePrefixes),
  });
  const [buffers, setBuffers] = useState({ categories: "", excludedRulePrefixes: "" });

  const numberChangeHandler = (e) => {
    const { name, value } = e.target;
    setYaraUI((prev) => ({ ...prev, [name]: value === "" ? "" : Number(value) }));
  };

  const boolChangeHandler = (e) => {
    const { name, value } = e.target;
    setYaraUI((prev) => ({ ...prev, [name]: value === "true" }));
  };

  const blurHandler = (e) => {
    const { name, value } = e.target;
    const limits = MIN_MAX_VALUES[name];
    if (!limits) return;

    const [min, max] = limits;
    let newValue = Number(value);

    if (newValue < min) newValue = min;
    if (newValue > max) newValue = max;

    setYaraUI((prev) => ({ ...prev, [name]: newValue }));
  };

  const bufferChangeHandler = (name, value) => {
    setBuffers((prev) => ({ ...prev, [name]: value }));
  };

  const addCsvItem = (name) => {
    const item = (buffers[name] || "").trim();

    if (item === "") {
      dispatch(
        addNotification({
          message: "Enter a valid value",
          options: { variant: "error" },
          time: 3000,
        }),
      );
      return;
    }
    if ((yaraUI[name] || []).some((existing) => existing === item)) {
      dispatch(
        addNotification({
          message: "Value already exists",
          options: { variant: "error" },
          time: 3000,
        }),
      );
      return;
    }

    setYaraUI((prev) => ({ ...prev, [name]: [...(prev[name] || []), item] }));
    setBuffers((prev) => ({ ...prev, [name]: "" }));
  };

  const removeCsvItem = (name, index) => {
    setYaraUI((prev) => ({ ...prev, [name]: prev[name].filter((_, i) => i !== index) }));
  };

  const checkSaveButtonDisabled = () => {
    if (!backend || !yaraUI) return true;

    for (const f of SCALAR_FIELDS) {
      if (BOOL_FIELDS.includes(f)) {
        if (toBool(backend[f]) !== yaraUI[f]) return false;
      } else if (backend[f] !== yaraUI[f]) {
        return false;
      }
    }

    for (const f of CSV_FIELDS) {
      const backendArr = toArray(backend[f]).slice().sort();
      const uiArr = [...(yaraUI[f] || [])].sort();
      if (backendArr.length !== uiArr.length) return false;
      if (!backendArr.every((v, i) => v === uiArr[i])) return false;
    }

    return true;
  };

  const updateAgentSettingsHandler = (e) => {
    e.preventDefault();
    if (updatePolicyLoading) return;

    dispatch(
      RequestUpdateAgentPolicy({
        id: policy.id,
        settings: {
          ...policy.settings,
          yara: {
            ...yaraUI,
            categories: yaraUI.categories.join(","),
            excludedRulePrefixes: yaraUI.excludedRulePrefixes.join(","),
          },
        },
        host: selectedHost,
      }),
    );
  };

  const toggleStatusHandler = () => {
    dispatch(
      RequestUpdateAgentPolicy({
        id: policy.id,
        settings: {
          ...policy.settings,
          yara: {
            ...policy.settings.yara,
            enabled: !policy?.settings?.yara?.enabled,
          },
        },
        host: selectedHost,
      }),
    );
  };

  const renderBool = (name, header, tooltip) => (
    <div className="agent-policy-form__line">
      <div className="mailing__content__security m-b-20">
        <p className="light m-b-10 mailing__content__security__header">
          {header}
          <Tooltip text={tooltip} />
        </p>
        <div className="mailing__content__security__labels">
          <label className="align-items">
            <input
              className="mailing__content__security__input"
              type="radio"
              value={true}
              name={name}
              checked={yaraUI[name] === true}
              onChange={boolChangeHandler}
            />
            <p className="margin-left-12">Enabled</p>
          </label>
          <label className="align-items">
            <input
              className="mailing__content__security__input"
              type="radio"
              value={false}
              name={name}
              checked={yaraUI[name] === false}
              onChange={boolChangeHandler}
            />
            <p className="margin-left-12">Disabled</p>
          </label>
        </div>
      </div>
    </div>
  );

  const renderCsvList = (name, label, tooltip, placeholder) => (
    <>
      <div className="agent-policy-form__group-line">
        <Field
          label={label}
          tooltipText={tooltip}
          width="300px"
          value={buffers[name]}
          onChange={(e) => bufferChangeHandler(name, e.target.value)}
          parentClassName="p-r-20"
          placeholder={placeholder}
        />
        <button
          type="button"
          className="products-body-form__button"
          onClick={() => addCsvItem(name)}>
          +
        </button>
      </div>

      <ul className="agent-policy-form__list" style={{ gridColumn: "span 2" }}>
        {yaraUI?.[name]?.length > 0 ? (
          yaraUI[name].map((item, index) => (
            <li key={index} className="agent-policy-form__list-item">
              {item}
              <button
                type="button"
                style={{ display: "flex" }}
                onClick={() => removeCsvItem(name, index)}>
                <TrashBucketIcon width="16" height="16" />
                <p style={{ marginLeft: "5px" }}>Delete</p>
              </button>
            </li>
          ))
        ) : (
          <li className="no-data">&nbsp;No Data</li>
        )}
      </ul>
    </>
  );

  return (
    <>
      <Header
        policy={policy}
        selectedHost={selectedHost}
        toggleProps={{
          show: true,
          text: "Yara Status",
          active: policy?.settings?.yara?.enabled,
          onClick: toggleStatusHandler,
        }}
      />

      <form className="agent-policy-form">
        {renderBool(
          "customRulesEnabled",
          "Custom Rules",
          "If enabled, custom YARA rules (yaras.yar) are also applied.",
        )}

        <div className="agent-policy-form__line">
          <Field
            label="Min Weight"
            tooltipText="Drop matches below this weight. Min: 0, Max: 100"
            width="220px"
            name="minWeight"
            value={yaraUI.minWeight}
            onChange={numberChangeHandler}
            onBlur={blurHandler}
            type="number"
          />
        </div>

        <div className="agent-policy-form__line">
          <Field
            label="Min File Size in bytes"
            tooltipText="Min: 1500, Max: 4500"
            width="220px"
            name="minFileSize"
            value={yaraUI.minFileSize}
            onChange={numberChangeHandler}
            onBlur={blurHandler}
            type="number"
          />
          <Field
            label="Max File Size in bytes"
            tooltipText="Min: 20800000, Max: 62400000"
            width="220px"
            name="maxFileSize"
            value={yaraUI.maxFileSize}
            onChange={numberChangeHandler}
            onBlur={blurHandler}
            type="number"
          />
        </div>

        <div className="agent-policy-form__line">
          <Field
            label="Memory Min File Size in bytes"
            tooltipText="Min: 4000, Max: 12000"
            width="220px"
            name="memMinFileSize"
            value={yaraUI.memMinFileSize}
            onChange={numberChangeHandler}
            onBlur={blurHandler}
            type="number"
          />
          <Field
            label="Memory Max File Size in bytes"
            tooltipText="Min: 30300000, Max: 90900000"
            width="220px"
            name="memMaxFileSize"
            value={yaraUI.memMaxFileSize}
            onChange={numberChangeHandler}
            onBlur={blurHandler}
            type="number"
          />
        </div>

        {renderCsvList(
          "categories",
          "Category",
          "Which YARA rule folders load (e.g., exe, doc, xls, msi, pdf, common)",
          "Enter a category (e.g., exe, pdf)",
        )}

        {renderCsvList(
          "excludedRulePrefixes",
          "Excluded Rule Prefix",
          "Matches whose rule name contains any of these substrings are dropped (case-sensitive)",
          "e.g. Debug, Win32_, SEH_",
        )}

        <button
          className="primary-button"
          disabled={checkSaveButtonDisabled()}
          onClick={updateAgentSettingsHandler}>
          Save
        </button>

        <Backdrop
          spinner={false}
          absolute
          show={!policy?.settings?.yara?.enabled}
          message="Yara settings are inactive! Please click on the status button at the header to activate yara settings."
          messageStyle={{ paddingTop: 0 }}
        />
      </form>
    </>
  );
};

export default YaraForm;
